import styles from "./GameCard.module.css"
import SupportedControls from "../SupportedControls/SupportedControls"
import PEGI, { PEGISize } from "../PEGI/PEGI"
import Label, { LabelKind } from "../Label/Label"
import { useState, useRef, useEffect } from "react"
import { Link } from '@/i18n/navigation'
import { GameBrief } from "@/interfaces/games_types"
import { ImageKey } from "@/interfaces/genericTypes.types"
import { useUserStore } from "@/stores/useUserStore"
import { pushToDataLayer } from "@/utils/helpers"
import Image from "next/image"
import { IMAGE_HEIGHT_HORIZONTAL, IMAGE_HEIGHT_VERTICAL, IMAGE_WIDTH_HORIZONTAL, IMAGE_WIDTH_VERTICAL } from "@/utils/constants"
import Icon from "../Icon/Icon"
import Icon_Maintenance from "@/assets/img/Icon_UnderMaintenance.svg"

export const CardType = {
	Horizontal: 0,
	Vertical: 1,
	Rounded: 2
}

export type CardType =
  (typeof CardType)[keyof typeof CardType]

type GameCardProps = {
	game: GameBrief,
	type?: CardType,
	columns?: number,
	mirror?: boolean,
	currentFirst?: boolean
	currentLast?: boolean,
	analyticsRow?: string,
	analyticsRowName?: string,
	analyticsModule?: string,
	analyticsModuleRow?: number,
	analyticsIndex?: number
}
export default function GameCard({ game, type = CardType.Horizontal, columns, mirror = false, currentFirst = false, currentLast = false, analyticsIndex, analyticsModule, analyticsModuleRow, analyticsRow, analyticsRowName } : GameCardProps) {

	const { userId, profileId } = useUserStore()
	const [hovered, setHovered] = useState(false)
	const hoverTimeout = useRef<NodeJS.Timeout | null>(null)
	const hasPushedHover = useRef(false)
	const sizes = [150, 133, 100, 75]
	let srcHorizontal = null
	let srcVertical = null
	
	if(game.thumb_horizontal) {
		for (const size of sizes) {
			const key = `esc_${String(size).padStart(3, '0')}_url` as ImageKey
			if (game.thumb_horizontal[key]) {
				srcHorizontal = game.thumb_horizontal[key]
				break
			}
		}
	}

	if(game.thumb_vertical) {
		for (const size of sizes) {
			const key = `esc_${String(size).padStart(3, '0')}_url` as ImageKey
			if (game.thumb_vertical[key]) {
				srcVertical = game.thumb_vertical[key]
				break
			}
		}
	}

	const src = type === CardType.Horizontal || type === CardType.Rounded ? srcHorizontal : srcVertical

	let style = { viewTransitionName: `gameImage_${game.game_id}` }
	if(columns !== undefined) style = { ...style, flex: `0 0 calc(100%/${columns})`}

	let labelKind = undefined
	if(game.recommended) labelKind = LabelKind.RECOMMENDED
	if(game.new_entry) labelKind = LabelKind.NEW
	if(game.type === 'steam') labelKind = LabelKind.STEAM_LICENSE
	if(game.type === 'epic') labelKind = LabelKind.EPIC_LICENSE
	if(game.type === 'battlenet') labelKind = LabelKind.BATTLENET_LICENSE


	function handleGameCardClick() {
		pushToDataLayer({
			event: 'game_card_click',
			game_id: game.game_id,
			game_name: game.title,
			module_name: analyticsModule,
			module_position: analyticsIndex,
			module_row: analyticsModuleRow,
			row_name: analyticsRow,
			card_type: type
		})
	}

	function handleMouseEnter() {
		setHovered(true)
		hasPushedHover.current = false
		if (hoverTimeout.current) clearTimeout(hoverTimeout.current)
		hoverTimeout.current = setTimeout(() => {
			hasPushedHover.current = true
			pushToDataLayer({
				event: 'home_element_visible',
				game_id: game.game_id,
				game_name: game.title,
				module_name: analyticsModule,
				module_position: analyticsIndex,
				module_row: analyticsModuleRow,
				row_id: analyticsRow,
				row_name: analyticsRowName,
				card_type: type
			})
		}, 2000)
	}

	function handleMouseLeave() {
		setHovered(false)
		if (hoverTimeout.current) {
			clearTimeout(hoverTimeout.current)
			hoverTimeout.current = null
		}
	}


	let imageWidth = 0
	if(type !== CardType.Vertical) imageWidth = IMAGE_WIDTH_HORIZONTAL
	else if(type === CardType.Vertical) imageWidth = IMAGE_WIDTH_VERTICAL

	let imageHeight = 0
	if(type !== CardType.Vertical) imageHeight = IMAGE_HEIGHT_HORIZONTAL
	else if(type === CardType.Vertical) imageHeight = IMAGE_HEIGHT_VERTICAL

	// const { width } = useWindowSize()
	// const expectedCardWidth = (width - 100) / columns!
	// const expectedCardHeight = type === CardType.Vertical ? expectedCardWidth * (IMAGE_WIDTH_VERTICAL / IMAGE_HEIGHT_VERTICAL) : expectedCardWidth * (IMAGE_WIDTH_HORIZONTAL / IMAGE_HEIGHT_HORIZONTAL)
	
	const [hasHover, setHasHover] = useState(false)

	useEffect(() => {
		setHasHover(window.matchMedia('(hover: hover)').matches)
	}, [])

	return (
		<Link
			onClick={handleGameCardClick}
			href={`/games/${game.game_id}`}
			className={`${styles.card} ${type === CardType.Vertical ? styles.vertical : styles.horizontal}`}
			style={style}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			data-testid={hovered ? 'showCard' : 'hiddenCard'}
		>
			{(game.status === 0 || game.status === 2) && !hovered && <Icon icon={Icon_Maintenance} className={styles.maintenanceIcon}/>}
			{src && <div className={`${game.blur_for_guests && !(userId && profileId) ? styles.blurContainer : ''}`}><Image quality={60} fetchPriority={analyticsModuleRow === 0 ? 'high' : 'auto' } src={process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + src} width={imageWidth} height={imageHeight} alt={game.title!} className={`${styles.cardImage} ${styles.base} ${game.blur_for_guests && !(userId && profileId) ? styles.blur: ''}`}/></div>}
			{labelKind !== undefined ? <div className={styles.label}><Label kind={labelKind} /></div> : null}
			{mirror && src && <Image width={imageWidth} height={imageHeight} src={process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + src} alt={game.title!} className={`${styles.cardImage} ${styles.mirror}`}/>}

			{hasHover && <div className={`${styles.content} cardContent`} style={ currentFirst ? { left : '0'} : currentLast ? { right : 0, left: 'unset' } : undefined } data-testid={hovered ? 'showCardContent' : 'hiddenCardContent'}>
				{(game.status === 0 || game.status === 2) && <Icon icon={Icon_Maintenance} className={styles.maintenanceIconHovered}/>}
				{srcHorizontal && <div className={`${game.blur_for_guests && !(userId && profileId) ? styles.blurContainer : ''}`}><Image quality={60} width={IMAGE_WIDTH_HORIZONTAL} height={IMAGE_HEIGHT_HORIZONTAL} src={process.env.NEXT_PUBLIC_ASSETS_ENDPOINT + srcHorizontal} alt={game.title!} className={`${styles.cardImage} ${game.blur_for_guests && !(userId && profileId) ? styles.blur: ''}`}/></div>}
				{(userId || (game && game.min_age && game.min_age < 18)) && hovered && !(game.blur_for_guests && !(userId && profileId)) &&
				<video playsInline src={game.trailer ? `${process.env.NEXT_PUBLIC_ASSETS_ENDPOINT}${game.trailer}` : undefined} autoPlay muted controls={false} loop className={`${styles.trailer} ${game.blur_for_guests && !(userId && profileId) ? styles.blur: ''}`} preload="none"/>
				}
				<p className={`${styles.contentTitle} bodyTextMedium`}>{game.title}</p>
				<div className={styles.data}>
					<SupportedControls type={game.supported_controls ?? -1} />
					<PEGI pegi={game.min_age ?? -1} size={PEGISize.SMALL}/>
				</div>
				{game.genres &&
				<div className={styles.genres}>
					{game.genres.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })).slice(0, 3).map(genre => {
						return (
							<span key={genre.id} className={`${styles.genre} bodyTextSmall`}>{genre.name}</span>
						)
					})}
				</div>
				}
			</div>}
		</Link>
	)
}